import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const Toast = ({ message, type = 'error', isVisible, onClose, duration = 5000 }) => {
  const { theme } = useTheme();
  const [animationState, setAnimationState] = useState('entering'); // 'entering', 'visible', 'exiting'
  
  useEffect(() => {
    if (isVisible) {
      // Start with entering animation
      setAnimationState('entering');
      
      // After a brief moment, show the toast fully
      const showTimer = setTimeout(() => {
        setAnimationState('visible');
      }, 100);
      
      // After duration, start exit animation
      if (duration > 0) {
        const exitTimer = setTimeout(() => {
          setAnimationState('exiting');
          
          // After exit animation completes, actually remove the toast
          const removeTimer = setTimeout(() => {
            onClose();
          }, 300); // Match this with the transition duration
          
          return () => clearTimeout(removeTimer);
        }, duration);
        
        return () => {
          clearTimeout(showTimer);
          clearTimeout(exitTimer);
        };
      }
      
      return () => clearTimeout(showTimer);
    }
  }, [isVisible, onClose, duration]);

  if (!isVisible) return null;

  const getToastStyles = () => {
    const baseStyles = "fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg border text-sm font-medium transition-all duration-300 ease-in-out max-w-md";
    
    const colorStyles = (() => {
      if (type === 'success') {
        return theme === "dark" 
          ? "bg-green-900 border-green-700 text-green-200"
          : "bg-green-50 border-green-200 text-green-800";
      } else if (type === 'error') {
        return theme === "dark" 
          ? "bg-red-900 border-red-700 text-red-200"
          : "bg-red-50 border-red-200 text-red-800";
      } else if (type === 'warning') {
        return theme === "dark" 
          ? "bg-yellow-900 border-yellow-700 text-yellow-200"
          : "bg-yellow-50 border-yellow-200 text-yellow-800";
      } else {
        return theme === "dark" 
          ? "bg-gray-800 border-gray-600 text-gray-200"
          : "bg-white border-gray-200 text-gray-800";
      }
    })();
    
    // Animation states
    const animationStyles = (() => {
      switch (animationState) {
        case 'entering':
          return 'transform translate-x-full scale-95 opacity-0';
        case 'visible':
          return 'transform translate-x-0 scale-100 opacity-100';
        case 'exiting':
          return 'transform translate-x-full scale-95 opacity-0';
        default:
          return 'transform translate-x-0 scale-100 opacity-100';
      }
    })();
    
    return `${baseStyles} ${colorStyles} ${animationStyles}`;
  };

  const getIcon = () => {
    const iconClass = "text-current";
    
    if (type === 'success') {
      return (
        <svg className={`w-5 h-5 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      );
    } else if (type === 'error') {
      return (
        <svg className={`w-5 h-5 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    } else if (type === 'warning') {
      return (
        <svg className={`w-5 h-5 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    } else {
      return (
        <svg className={`w-5 h-5 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
  };

  const handleClose = () => {
    setAnimationState('exiting');
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div className={getToastStyles()}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">{message}</div>
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 ml-4 text-current hover:opacity-70 transition-opacity"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;